import { FC, Fragment, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Chip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';

import {
  getMapLabelByOperationType,
  OperationTypesEnum,
} from '../constants/shared.js';
import type { EntityGroupType } from '../types/shared.js';

type Props = {
  items: EntityGroupType[];
  maxVisibleChipsLimit?: number;
};

const EntityGroup: FC<Props> = ({ items, maxVisibleChipsLimit }) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');

  const mapLabelByOperationType = getMapLabelByOperationType(t);
  const separatorLabel =
    mapLabelByOperationType[OperationTypesEnum.enum.or].toUpperCase();

  const { visibleItems, remainingCount, tooltipText } = useMemo(() => {
    const visible = items.slice(0, maxVisibleChipsLimit);
    const hidden = maxVisibleChipsLimit
      ? items.slice(maxVisibleChipsLimit)
      : [];

    const visibleItems = visible.map((item) => {
      if (item.isCurrentUser) {
        return {
          ...item,
          label: t('{{label}} (You)', { label: item.label }),
        };
      }
      return item;
    });
    const tooltipText = hidden
      .map((item) => {
        return item.isCurrentUser
          ? t('{{label}} (You)', { label: item.label })
          : item.label;
      })
      .join(` ${separatorLabel} `);

    return {
      visibleItems,
      remainingCount: hidden.length,
      tooltipText,
    };
  }, [items, maxVisibleChipsLimit, separatorLabel, t]);

  const showRemainingChipCount = maxVisibleChipsLimit && remainingCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {visibleItems.map((item, index) => {
        return (
          <Fragment key={item.value}>
            {index === 0 ? null : (
              <span
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'secondary',
                })}
              >
                {separatorLabel}
              </span>
            )}
            <Chip variant="outline" size="medium" color="silver">
              {item.label}
            </Chip>
          </Fragment>
        );
      })}
      {showRemainingChipCount ? (
        <>
          <span
            className={typographyVariants({
              variant: 'bodyShort01',
              color: 'secondary',
            })}
          >
            {separatorLabel}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Chip variant="outline" color="silver" size="medium">
                +{remainingCount}
              </Chip>
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        </>
      ) : null}
    </div>
  );
};

export default EntityGroup;
